import { Table } from '@/components';
import { loadingTableHandler } from '@/utils/handler/loadingTableHandler';
import { Dropdown, Tag } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ICustomer } from '../../types';
import { ROUTESPATH } from '@/routes/constant';


interface CustomerTableProps {
  isLoading: boolean;
  data: ICustomer[];
  current?: number;
  pageSize?: number;
  total?: number;
  nextLink?: string;
  totalRecordCountLimitExceeded?: boolean;
  handleChange?: (page: number, pageSize: number) => void;
}

export const CustomerTable: React.FC<CustomerTableProps> = ({ isLoading, data, current, pageSize, total, nextLink, totalRecordCountLimitExceeded, handleChange }) => {
  const navigate = useNavigate();

  const handleViewDetails = (accountId: string) => {
    navigate(`/customers/${accountId}`);
  };

  const columns: ColumnsType<ICustomer> = [
    {
      title: 'Customer Name',
      dataIndex: 'name',
      key: 'name',
      className: 'font-semibold',
    },
    {
      title: 'Contact Email',
      dataIndex: ['primarycontactid', 'emailaddress1'],
      key: 'emailaddress1',
      render: (text: string) => text || '-',
    },
    {
      title: 'Phone',
      dataIndex: ['primarycontactid', 'telephone1'],
      key: 'telephone1',
      render: (text: string) => text || '-',
    },
    {
      title: 'Address',
      dataIndex: 'address1_composite',
      key: 'address1_line1',
      render: (value) => value || '-',
    },
    {
      title: 'Customer Type',
      dataIndex: 'customertypecode@OData.Community.Display.V1.FormattedValue',
      key: 'customertypecode',
      render: (customertypecode) => (
        <Tag>{customertypecode}</Tag>
      ),
    },
    {
      title: 'Credit Terms',
      dataIndex: 'paymenttermscode@OData.Community.Display.V1.FormattedValue',
      key: 'paymenttermscode',
    },

    {
      title: 'Region',
      dataIndex: 'dogma_region@OData.Community.Display.V1.FormattedValue',
      key: 'dogma_region',
    },
    {
      title: 'Status',
      dataIndex: 'statecode',
      key: 'statecode',
      render: (status: number, record: ICustomer) => {
        let color = 'default';
        if (status === 1) {
          color = 'red';
        } else if (status === 0) {
          color = 'green';
        }

        return (
          <Tag color={color} style={{ fontWeight: 500 }}>
            {record['statecode@OData.Community.Display.V1.FormattedValue'] || 'Unknown'}
          </Tag>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right' as const,
      width: 80,
      render: (_: any, record: ICustomer) => (
        <Dropdown
          rootClassName="row_actions_dropdown"
          menu={{
            items: [
              {
                key: 'view',
                label: 'View Details',
                onClick: () => handleViewDetails(record.accountid),
              },
              {
                key: 'discount',
                label: 'Configure Discount',
                onClick: () => navigate(ROUTESPATH.CUSTOMER_DISCOUNT.replace(':accountId', record.accountid), { state: { from: 'customers' } }),
              },
            ],
          }}
        >
          <MoreOutlined />
        </Dropdown>
      ),
    },
  ];

  return loadingTableHandler(
    isLoading,
    <Table<ICustomer>
      columns={columns}
      dataSource={data}
      rowKey="accountid"
      isLoading={isLoading}
      current={current}
      pageSize={pageSize}
      total={total}
      nextLink={nextLink}
      totalRecordCountLimitExceeded={totalRecordCountLimitExceeded}
      handleChange={handleChange}
    />
  );
};
